import { ActivityOverviewSupportDto } from '@api/FitnessActivityClient';
import React, { useMemo } from 'react';
import { Line, LineChart, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@components/ui/card';
import {
    ChartConfig,
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent
} from '@components/ui/chart';
import { cn } from '@core/utils/cn';
import {
    useTimeframeData,
    buildChartSeries,
    generateXAxisData,
    getDayBounds,
    formatTime
} from '../utils/TimeframeChart.utils';

interface TimeframeChartProps {
    data: ActivityOverviewSupportDto | null;
    isOnlyDay?: boolean;
}

const seriesKey = (index: number) => `s${index}`;

export function TimeframeChart({
    data,
    isOnlyDay = false
}: TimeframeChartProps) {
    const timeframeData = useTimeframeData({ data });
    const userDayDate = new Date(data?.userDay ?? '');
    const xAxisData = useMemo(
        () => generateXAxisData(timeframeData),
        [timeframeData]
    );
    const chartSeries = useMemo(
        () => buildChartSeries(timeframeData, xAxisData),
        [timeframeData, xAxisData]
    );
    const dayBounds = useMemo(() => getDayBounds(userDayDate), [data?.userDay]);

    const rows = useMemo(
        () =>
            xAxisData.map((time, rowIndex) => {
                const row: Record<string, number | null> = {
                    time: time.getTime()
                };
                chartSeries.forEach((series, seriesIndex) => {
                    row[seriesKey(seriesIndex)] = series.data[rowIndex];
                });
                return row;
            }),
        [xAxisData, chartSeries]
    );

    const chartConfig = useMemo<ChartConfig>(
        () =>
            Object.fromEntries(
                chartSeries.map((series, index) => [
                    seriesKey(index),
                    { label: series.label, color: series.color }
                ])
            ),
        [chartSeries]
    );

    if (timeframeData.length === 0) {
        return null;
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle>Activity Timeframes</CardTitle>
            </CardHeader>
            <CardContent>
                <ChartContainer
                    config={chartConfig}
                    className={cn(
                        'w-full',
                        isOnlyDay ? 'aspect-auto h-[300px]' : 'aspect-video'
                    )}
                >
                    <LineChart
                        data={rows}
                        margin={{ left: 0, right: 20, top: 20, bottom: 10 }}
                    >
                        <XAxis
                            dataKey="time"
                            type="number"
                            scale="time"
                            domain={[
                                dayBounds.min.getTime(),
                                dayBounds.max.getTime()
                            ]}
                            tickFormatter={(value: number) =>
                                formatTime(new Date(value))
                            }
                        />
                        <YAxis
                            width={40}
                            domain={[0, timeframeData.length + 1]}
                            allowDecimals={false}
                        />
                        <ChartTooltip
                            content={
                                <ChartTooltipContent
                                    hideLabel
                                    formatter={(value, name, item) => (
                                        <div className="flex items-center gap-2">
                                            <span
                                                className="size-2.5 shrink-0 rounded-[2px]"
                                                style={{
                                                    backgroundColor: item.color
                                                }}
                                            />
                                            <span className="text-muted-foreground">
                                                {chartConfig[String(name)]
                                                    ?.label ?? name}
                                            </span>
                                            <span className="ml-auto font-mono font-medium text-foreground">
                                                {chartSeries[
                                                    Number(
                                                        String(name).slice(1)
                                                    )
                                                ]?.valueFormatter(
                                                    typeof value === 'number'
                                                        ? value
                                                        : null
                                                )}
                                            </span>
                                        </div>
                                    )}
                                />
                            }
                        />
                        {chartSeries.map((series, index) => (
                            <Line
                                key={seriesKey(index)}
                                dataKey={seriesKey(index)}
                                type="linear"
                                stroke={series.color}
                                strokeWidth={4}
                                strokeLinecap="round"
                                connectNulls={false}
                                dot={false}
                                isAnimationActive={false}
                            />
                        ))}
                    </LineChart>
                </ChartContainer>
            </CardContent>
        </Card>
    );
}
